import { useSyncExternalStore } from 'react';
import type { Appearance } from '@stripe/stripe-js';
import { useThemeStore } from '@/stores/theme-store';

/**
 * Resolve theme + system pref to a single dark boolean.
 * Subscribes to both the theme store and `prefers-color-scheme` so the value
 * updates when either changes (matters in `system` mode while the form is open).
 */
function subscribeIsDark(callback: () => void): () => void {
  const unsubStore = useThemeStore.subscribe(callback);
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  mq.addEventListener('change', callback);
  return () => {
    unsubStore();
    mq.removeEventListener('change', callback);
  };
}

function getIsDarkSnapshot(): boolean {
  const theme = useThemeStore.getState().theme;
  if (theme === 'dark') return true;
  if (theme === 'system') return window.matchMedia('(prefers-color-scheme: dark)').matches;
  return false;
}

/**
 * Returns a Stripe `Appearance` config that matches scio3's resolved theme.
 *
 * Returns `isDark` separately so callers can re-key `<Elements>` on theme
 * change — Stripe Elements only re-applies appearance when the component
 * remounts.
 */
export function useStripeAppearance(): { appearance: Appearance; isDark: boolean } {
  const isDark = useSyncExternalStore(subscribeIsDark, getIsDarkSnapshot, getIsDarkSnapshot);
  // Shared rules — no drop shadows on inputs or tab buttons, in both light and dark mode.
  const inputRules = {
    '.Input': { boxShadow: 'none' },
    '.Input:focus': { boxShadow: 'none' },
    '.Tab': { boxShadow: 'none' },
    '.Tab:hover': { boxShadow: 'none' },
    '.Tab:focus': { boxShadow: 'none' },
  };

  const appearance: Appearance = isDark
    ? {
        theme: 'night',
        variables: {
          borderRadius: '6px',
          colorPrimary: '#1ab394',
        },
        rules: {
          ...inputRules,
          // Keep selected tab's gray bg (matching unselected) but add teal outline + label
          '.Tab--selected': {
            backgroundColor: '#30313d',
            borderColor: '#1ab394',
            color: '#1ab394',
            boxShadow: 'none',
          },
          '.Tab--selected:focus': {
            backgroundColor: '#30313d',
            borderColor: '#1ab394',
            color: '#1ab394',
            boxShadow: 'none',
          },
          '.TabIcon--selected': { fill: '#1ab394' },
          '.TabLabel--selected': { color: '#1ab394' },
        },
      }
    : {
        theme: 'stripe',
        variables: {
          borderRadius: '6px',
          colorPrimary: '#1ab394',
        },
        rules: inputRules,
      };
  return { appearance, isDark };
}
